import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Phone, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/contexts/AuthContext';

const UserProfileModal = ({ open, onClose, user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    password: ''
  });

  useEffect(() => {
    if (open) {
      fetchUserStats();
      setEditing(false);
    }
  }, [open]);

  useEffect(() => {
    if (stats) {
      setFormData({
        name: stats.name || '',
        phone: stats.phone || '',
        password: ''
      });
    }
  }, [stats]);

  const fetchUserStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/users/me/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      toast.error('Erro ao carregar informações do perfil');
    } finally {
      setLoading(false);
    }
  };

  const getRoleName = (role) => {
    const roles = {
      'master': 'Master',
      'comercial': 'Comercial',
      'administrativo': 'Administrativo',
      'sales_ops': 'Sales Ops',
      'financiamentos': 'Financiamentos'
    };
    return roles[role] || role;
  };

  const handleSave = async () => {
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Nome é obrigatório');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone || null
      };

      // Só enviar senha se foi preenchida
      if (formData.password && formData.password.trim() !== '') {
        payload.password = formData.password;
      }

      await axios.patch(`${API_URL}/users/${stats.id || user.id}`, payload);

      toast.success('Perfil atualizado com sucesso!');
      setEditing(false);
      fetchUserStats();
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Meu perfil</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-2">
          {loading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Carregando...</p>
            </div>
          ) : stats ? (
            <div className="space-y-6">
            {/* Avatar e Nome */}
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-medium mb-3">
                {(formData.name || stats.name) ? 
                  (formData.name || stats.name).split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 
                  stats.email?.[0]?.toUpperCase() || 'U'
                }
              </div>
              
              {!editing ? (
                <>
                  <h3 className="text-xl font-semibold">
                    {stats.name || 'Usuário sem nome'}
                  </h3>
                  <span className="mt-1 text-[13px] text-gray-500">
                    {getRoleName(stats.role)}
                  </span>
                </>
              ) : (
                <div className="w-full mt-2">
                  <Label htmlFor="edit-name">Nome completo *</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Digite seu nome"
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            {/* Informações de Contato */}
            <div className="space-y-3 rounded-lg bg-sheet2 p-4">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-gray-400" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                  <p className="text-gray-900 dark:text-gray-100 font-medium">{stats.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-gray-400" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Telefone</p>
                  {!editing ? (
                    <p className="text-gray-900 dark:text-gray-100 font-medium">
                      {stats.phone || 'Não informado'}
                    </p>
                  ) : (
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      className="mt-1"
                    />
                  )}
                </div>
              </div>

              {editing && (
                <div className="flex items-center gap-3 text-sm pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex-1">
                    <Label htmlFor="edit-password">Nova Senha (opcional)</Label>
                    <Input
                      id="edit-password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Deixe em branco para não alterar"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Preencha apenas se quiser alterar sua senha
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Estatísticas de Pedidos (apenas para comercial) */}
            {stats.role === 'comercial' && stats.total_pedidos !== undefined && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Estatísticas</h4>
                
                <dl className="grid grid-cols-3 divide-x divide-border rounded-lg bg-sheet2">
                  {[
                    [stats.total_pedidos, 'pedidos'],
                    [stats.pedidos_pendentes, 'pendentes'],
                    [stats.pedidos_confirmados, 'confirmados'],
                  ].map(([valor, rotulo]) => (
                    <div key={rotulo} className="flex flex-col-reverse px-3 py-3 text-center">
                      <dt className="text-[12.5px] text-gray-500">{rotulo}</dt>
                      <dd className="text-[22px] font-medium leading-tight tabular-nums">{valor}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center">
            <p className="text-gray-500">Erro ao carregar dados</p>
          </div>
        )}
        </div>

        {stats && (
          <DialogFooter className="gap-2 pt-4 border-t">
            {!editing ? (
              <Button onClick={() => setEditing(true)} className="w-full sm:w-auto">
                <Edit2 className="h-4 w-4 mr-2" />
                Editar Perfil
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      name: stats.name || '',
                      phone: stats.phone || '',
                      password: ''
                    });
                  }}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileModal;
